// /app/bulk — Bulk generation wizard, restyled from the 2026-05-22 handoff.
//
// Per design/Flows.html Flow 04. Linear stepper: brand → range & theme →
// platform mix → run. Polls for progress until terminal status.
//
// Backend wiring (PRESERVED):
//   POST /v1/brands/:id/bulk-generations   — submit job
//   GET  /v1/bulk-jobs/:id (refetch every 5s) — poll progress

import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/lib/api";
import { GRAPHIC_MODELS, DESIGN_STYLES } from "@/lib/image-options";
import { useEngineHealth, maintenanceSuffix } from "@/hooks/use-engine-health";
import { useBrands } from "@/hooks/use-persona";
import { useActiveBrand } from "@/stores/active-brand";
import { Icons, PlatformIcon, Rosette } from "@/components/icons";
import {
  BrandMark,
  PageHead,
  PlatformChip,
  Toggle,
  commentsDisabledHint,
} from "@/components/primitives";
import type { Brand, Platform } from "@/lib/types";

export const Route = createFileRoute("/app/bulk")({
  component: BulkWizard,
});

interface BulkJob {
  id: string;
  brand_id: string;
  start_date: string;
  end_date: string;
  platforms: string[];
  count_per_platform: Record<string, number>;
  include_holidays: boolean;
  prompt?: string | null;
  status: "pending" | "running" | "completed" | "partial" | "failed";
  created_at: string;
  completed_at?: string | null;
  child_stats: {
    total?: number;
    pending: number;
    running: number;
    ready?: number;
    approved?: number;
    failed: number;
  };
}

const ALL_PLATFORMS: Platform[] = ["instagram", "facebook", "x", "linkedin", "telegram"];

const PLATFORM_META: Record<Platform, { name: string; short: string; color: string }> = {
  instagram: { name: "Instagram", short: "IG", color: "#E1306C" },
  facebook: { name: "Facebook", short: "FB", color: "#1877F2" },
  x: { name: "X", short: "X", color: "#FFFFFF" },
  linkedin: { name: "LinkedIn", short: "LI", color: "#0A66C2" },
  telegram: { name: "Telegram", short: "TG", color: "#26A5E4" },
};

type Step = 1 | 2 | 3 | 4;

// Exported so the Generate screen can embed it under the Tek/Toplu toggle
// (the two are now one menu item). The /app/bulk route still renders it for
// backward-compatible deep links. v0.1.x (2026-07-09).
export function BulkWizard() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";

  const { data: brandsData } = useBrands();
  const brands = brandsData?.data ?? [];
  const activeBrand = useActiveBrand((s) => s.activeBrand);

  const [step, setStep] = useState<Step>(1);
  const [brandId, setBrandId] = useState(activeBrand?.id ?? "");
  const [startDate, setStartDate] = useState(toISODate(new Date()));
  const [endDate, setEndDate] = useState(toISODate(addDays(new Date(), 30)));
  // Start empty — no pre-filled example. The textarea placeholder guides.
  const [prompt, setPrompt] = useState("");
  const [includeHolidays, setIncludeHolidays] = useState(true);
  // v0.1.114 — publish every post in this job with comments/replies closed.
  // Only x + instagram can honour it; see commentsDisabledHint.
  const [disableComments, setDisableComments] = useState(false);
  // image_type for ALL children of this bulk job. Default text_based
  // (existing Claude-HTML composer). Graphical routes to fal.ai for
  // photoreal / cinematic output. v0.2.92.
  const [imageType, setImageType] = useState<"text_based" | "graphical">("text_based");
  // v0.1.x — graphical model pick + design style for ALL children ('' = auto).
  const [imageEngine, setImageEngine] = useState("");
  const [designStyle, setDesignStyle] = useState("");
  // post_type for the whole bulk job: 'feed' or 'story'. v0.1.125 —
  // story is the INTENT: IG/FB children render true 9:16 Stories, every
  // other platform resolves to its normal feed shape server-side.
  const [postType, setPostType] = useState<"feed" | "story">("feed");
  // Per-platform counts derive from the date range × a best-practice weekly
  // posting cadence (below) — not fixed numbers. Recomputed whenever the
  // range changes, until the operator manually adjusts a stepper.
  const [counts, setCounts] = useState<Record<string, number>>(() =>
    suggestedCounts(daysBetween(toISODate(new Date()), toISODate(addDays(new Date(), 30)))),
  );
  const [countsDirty, setCountsDirty] = useState(false);

  // Re-suggest counts from the chosen range (days × cadence) unless the
  // operator has hand-edited the steppers.
  useEffect(() => {
    if (countsDirty) return;
    setCounts(suggestedCounts(daysBetween(startDate, endDate)));
  }, [startDate, endDate, countsDirty]);

  const brand = brands.find((b) => b.id === brandId) ?? null;
  const total = Object.values(counts).reduce((s, v) => s + v, 0);
  const platforms = (Object.keys(counts) as Platform[]).filter((p) => counts[p] > 0);

  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => {
      // v0.1.125 — story mode keeps EVERY platform: the backend renders
      // true Stories on IG/FB and normal feed posts elsewhere
      // (effectivePostType, content v0.3.66). No more silent dropping.
      const effectivePlatforms = platforms;
      const count: Record<string, number> = {};
      for (const p of effectivePlatforms) count[p] = counts[p];
      return api<BulkJob>(`/brands/${brandId}/bulk-generations`, {
        method: "POST",
        body: {
          start_date: startDate,
          end_date: endDate,
          platforms: effectivePlatforms,
          count_per_platform: count,
          include_holidays: includeHolidays,
          comments_disabled: disableComments,
          prompt: prompt.trim(),
          post_type: postType,
          image_type: imageType,
          image_engine: imageType === "graphical" ? imageEngine : "",
          design_style: imageType === "text_based" ? designStyle : "",
        },
        idempotencyKey: crypto.randomUUID(),
      });
    },
    onSuccess: (j) => {
      setActiveJobId(j.id);
      setStep(4);
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={tr ? "06 · Toplu plan" : "06 · Bulk plan"}
        title={tr ? "Bir oturuşta bir ayı planla." : "Plan a month in one sitting."}
        subtitle={
          tr
            ? "Marka seç, tarih aralığını ve temayı belirle, platform başına kaç gönderi olacağına karar ver. Varyantlar arka planda akar."
            : "Pick a brand, set a date range and theme, decide how many per platform. Variants stream while you do other things."
        }
      />

      <Stepper step={step} tr={tr} />

      <div className="scroll-y" style={{ flex: 1, padding: "32px 32px 48px" }}>
        {step === 1 && (
          <BrandStep
            brands={brands}
            selected={brandId}
            onSelect={setBrandId}
            onNext={() => setStep(2)}
            tr={tr}
          />
        )}

        {step === 2 && (
          <RangeStep
            startDate={startDate}
            endDate={endDate}
            onStart={setStartDate}
            onEnd={setEndDate}
            prompt={prompt}
            onPrompt={setPrompt}
            includeHolidays={includeHolidays}
            onIncludeHolidays={setIncludeHolidays}
            disableComments={disableComments}
            onDisableComments={setDisableComments}
            selectedPlatforms={platforms}
            imageType={imageType}
            onImageType={setImageType}
            imageEngine={imageEngine}
            onImageEngine={setImageEngine}
            designStyle={designStyle}
            onDesignStyle={setDesignStyle}
            postType={postType}
            onPostType={setPostType}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            tr={tr}
          />
        )}

        {step === 3 && (
          <MixStep
            brand={brand}
            counts={counts}
            onChange={(c) => {
              setCountsDirty(true);
              setCounts(c);
            }}
            total={total}
            onBack={() => setStep(2)}
            onRun={() => submit.mutate()}
            submitting={submit.isPending}
            submitError={submit.error instanceof ApiError ? submit.error.message : null}
            tr={tr}
          />
        )}

        {step === 4 && activeJobId && brand && (
          <RunStep jobId={activeJobId} brand={brand} counts={counts} total={total} tr={tr} />
        )}
      </div>
    </div>
  );
}

function Stepper({ step, tr }: { step: Step; tr: boolean }) {
  const steps = [
    { n: 1, label: tr ? "Marka" : "Brand" },
    { n: 2, label: tr ? "Aralık ve tema" : "Range & theme" },
    { n: 3, label: tr ? "Karışım" : "Mix" },
    { n: 4, label: tr ? "Çalıştır" : "Run" },
  ];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "20px 32px",
        borderBottom: "1px solid var(--b1)",
        gap: 0,
      }}
    >
      {steps.map((s, i) => (
        <span
          key={s.n}
          style={{ display: "contents" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: step >= s.n ? "var(--tx)" : "var(--mu)",
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background:
                  step > s.n
                    ? "var(--accent)"
                    : step === s.n
                      ? "var(--accent-soft)"
                      : "var(--elev)",
                border: `1px solid ${step >= s.n ? "var(--accent)" : "var(--b2)"}`,
                color:
                  step > s.n
                    ? "#fff"
                    : step === s.n
                      ? "var(--accent)"
                      : "var(--mu)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 600,
                fontFamily: "var(--mono)",
              }}
            >
              {step > s.n ? <Icons.Check size={12} /> : s.n}
            </div>
            <span style={{ fontSize: 13, fontWeight: step === s.n ? 600 : 500 }}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 ? (
            <div
              style={{
                flex: 1,
                height: 1,
                background: step > s.n ? "var(--accent)" : "var(--b1)",
                margin: "0 14px",
                transition: "background 250ms",
              }}
            />
          ) : null}
        </span>
      ))}
    </div>
  );
}

// ── Step 1: Brand pick ────────────────────────────────────────────────────

function BrandStep({
  brands,
  selected,
  onSelect,
  onNext,
  tr,
}: {
  brands: Brand[];
  selected: string;
  onSelect: (id: string) => void;
  onNext: () => void;
  tr: boolean;
}) {
  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontSize: 22, marginBottom: 6 }}>
        {tr ? "Hangi marka için planlıyoruz?" : "Which brand are we planning for?"}
      </h2>
      <p style={{ color: "var(--tx2)", marginBottom: 24 }}>
        {tr
          ? "Toplu işler tek bir markaya odaklanır. Paralel olarak birden fazla başlatabilirsin."
          : "Bulk jobs scope to one brand at a time. You can run multiple in parallel."}
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 10,
        }}
      >
        {brands.map((b) => {
          const on = selected === b.id;
          const accent = b.accent_hex || "var(--accent)";
          const accentSoft = b.accent_soft_hex || "var(--accent-soft)";
          return (
            <button
              key={b.id}
              onClick={() => onSelect(b.id)}
              style={{
                padding: 14,
                display: "flex",
                alignItems: "center",
                gap: 12,
                textAlign: "left",
                background: on
                  ? `linear-gradient(90deg, ${accentSoft}, transparent)`
                  : "var(--surface)",
                border: `1px solid ${on ? accent : "var(--b2)"}`,
                borderRadius: 10,
              }}
            >
              <BrandMark brand={b} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{b.trade_name}</div>
                <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                  {b.industry ?? ""}
                </div>
              </div>
              {on ? <Icons.Check size={16} style={{ color: accent }} /> : null}
            </button>
          );
        })}
        {brands.length === 0 ? (
          <p style={{ color: "var(--mu)", fontSize: 13 }}>
            {tr ? "Henüz marka yok." : "No brands yet."}
          </p>
        ) : null}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
        <button className="btn btn-primary" onClick={onNext} disabled={!selected}>
          {tr ? "Devam" : "Continue"} <Icons.ArrowRight size={13} />
        </button>
      </div>
    </div>
  );
}

// ── Step 2: Date range + theme ────────────────────────────────────────────

function RangeStep({
  startDate,
  endDate,
  onStart,
  onEnd,
  prompt,
  onPrompt,
  includeHolidays,
  onIncludeHolidays,
  disableComments,
  onDisableComments,
  selectedPlatforms,
  imageType,
  onImageType,
  imageEngine,
  onImageEngine,
  designStyle,
  onDesignStyle,
  postType,
  onPostType,
  onBack,
  onNext,
  tr,
}: {
  startDate: string;
  endDate: string;
  onStart: (v: string) => void;
  onEnd: (v: string) => void;
  prompt: string;
  onPrompt: (v: string) => void;
  includeHolidays: boolean;
  onIncludeHolidays: (v: boolean) => void;
  disableComments: boolean;
  onDisableComments: (v: boolean) => void;
  selectedPlatforms: Platform[];
  imageType: "text_based" | "graphical";
  onImageType: (v: "text_based" | "graphical") => void;
  imageEngine: string;
  onImageEngine: (v: string) => void;
  designStyle: string;
  onDesignStyle: (v: string) => void;
  postType: "feed" | "story";
  onPostType: (v: "feed" | "story") => void;
  onBack: () => void;
  onNext: () => void;
  tr: boolean;
}) {
  // Provider maintenance (v0.1.119) — see hooks/use-engine-health.ts.
  // Same rules as the Generate screen: disabled options, never auto-select
  // one, explain the swap. Empty set until the endpoint answers, so a slow
  // or failed read leaves the picker exactly as it was.
  const engineHealth = useEngineHealth();
  const [engineDown, setEngineDown] = useState(false);
  useEffect(() => {
    if (!imageEngine || !engineHealth.unavailable.has(imageEngine)) return;
    setEngineDown(true);
    // '' = Otomatik — always safe, the backend roster picks a healthy engine.
    onImageEngine("");
  }, [imageEngine, engineHealth.unavailable, onImageEngine]);

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontSize: 22, marginBottom: 6 }}>
        {tr ? "Aralığı ve temayı belirle." : "Set the window and theme."}
      </h2>
      <p style={{ color: "var(--tx2)", marginBottom: 24 }}>
        {tr
          ? "Aralık ne kadar uzunsa, tematik olarak o kadar çok malzemeye ihtiyacımız olur."
          : "The longer the window, the more material we need to spread thematically."}
      </p>

      <div className="card" style={{ padding: 22, marginBottom: 14 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          — {tr ? "Tarih aralığı" : "Date range"}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <div>
            <label
              className="mono"
              style={{
                fontSize: 10,
                color: "var(--mu)",
                letterSpacing: "0.1em",
                display: "block",
                marginBottom: 4,
              }}
            >
              {tr ? "BAŞLANGIÇ" : "FROM"}
            </label>
            <input
              type="date"
              className="input"
              value={startDate}
              onChange={(e) => onStart(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label
              className="mono"
              style={{
                fontSize: 10,
                color: "var(--mu)",
                letterSpacing: "0.1em",
                display: "block",
                marginBottom: 4,
              }}
            >
              {tr ? "BİTİŞ" : "TO"}
            </label>
            <input
              type="date"
              className="input"
              value={endDate}
              onChange={(e) => onEnd(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <Toggle
          on={includeHolidays}
          onChange={onIncludeHolidays}
          label={
            tr
              ? "Türk milli bayramları + dini günler için özel içerik üret"
              : "Include Turkish national holidays + observances"
          }
        />

        {/* v0.1.114 — close the comment/reply section on every post in
            this job. Honoured on X + Instagram only; the hint names the
            platforms that will actually apply it. */}
        <div style={{ marginTop: 14 }}>
          <Toggle
            on={disableComments}
            onChange={onDisableComments}
            label={tr ? "Yorumları kapat" : "Disable comments"}
          />
          <div style={{ fontSize: 10, color: "var(--mu)", marginTop: 4 }}>
            {commentsDisabledHint(selectedPlatforms, tr)}
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          — {tr ? "Tema / konu" : "Theme / topic"}
        </div>
        <textarea
          className="input"
          value={prompt}
          onChange={(e) => onPrompt(e.target.value)}
          rows={5}
          placeholder={
            tr
              ? "Bu dönem için tema veya konu — örneğin bir kampanya, sezon ya da öne çıkarmak istediklerin."
              : "Theme or topic for this run — e.g. a campaign, season, or what you want to highlight."
          }
          style={{ resize: "vertical", width: "100%" }}
        />
        <div style={{ fontSize: 11, color: "var(--tx2)", marginTop: 8, lineHeight: 1.55 }}>
          {tr ? (
            <>
              Kampanyanın amacını <strong>ve önemli tarih/saati</strong> buraya yaz
              (ör. “3 Temmuz Cumartesi 10:00’da lansman”). Böylece gönderiler aynı
              cümleyi tekrarlamaz: önce teaser ve geri sayım, lansman günü “bugün
              açılıyoruz”, sonrasında “artık yayındayız”. İçeriği{" "}
              <strong>marka dilinde</strong> yaz.
            </>
          ) : (
            <>
              Write the campaign goal <strong>and the key date/time</strong> here
              (e.g. “Launch Sat 3 July, 10:00”). That keeps posts from repeating the
              same line — teasers &amp; countdown first, one launch-day post, then
              post-launch follow-ups. Write it in your{" "}
              <strong>brand’s language</strong>.
            </>
          )}
        </div>
      </div>

      {/* Image type chip — applies to every child generation in this
          bulk job. Default text_based; switch to graphical when the
          whole campaign needs photoreal/cinematic output. */}
      <div className="card" style={{ padding: 22, marginTop: 14 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          — {tr ? "Görsel türü" : "Image type"}
        </div>
        <div
          style={{
            display: "flex",
            background: "var(--surface)",
            border: "1px solid var(--b2)",
            borderRadius: 8,
            padding: 3,
          }}
        >
          {(
            [
              [
                "text_based",
                tr ? "Tipografi" : "Text-based",
                tr ? "Marka renkleri · Logo · Tipografi" : "Brand colors · Logo · Typography",
              ],
              [
                "graphical",
                tr ? "Grafik" : "Graphical",
                tr ? "Fotoğrafik · Sinematik (fal.ai)" : "Photoreal · Cinematic (fal.ai)",
              ],
            ] as const
          ).map(([id, label, sub]) => (
            <button
              key={id}
              onClick={() => onImageType(id)}
              style={{
                flex: 1,
                padding: "10px 12px",
                borderRadius: 6,
                fontSize: 13,
                background: imageType === id ? "var(--elev)" : "transparent",
                color: imageType === id ? "var(--tx)" : "var(--tx2)",
                border:
                  imageType === id ? "1px solid var(--b3)" : "1px solid transparent",
                fontWeight: imageType === id ? 600 : 500,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span>{label}</span>
              <span
                style={{ fontSize: 10, color: "var(--mu)", fontFamily: "var(--mono)" }}
              >
                {sub}
              </span>
            </button>
          ))}
        </div>
        {/* v0.1.x — model (Grafik) / style (Tipografi) picker for the whole job. */}
        {imageType === "graphical" ? (
          <>
          <select
            value={imageEngine}
            onChange={(e) => {
              setEngineDown(false);
              onImageEngine(e.target.value);
            }}
            style={{ width: "100%", marginTop: 12, padding: "9px 10px", borderRadius: 8, fontSize: 13, background: "var(--surface)", border: "1px solid var(--b2)", color: "var(--tx)" }}
          >
            {GRAPHIC_MODELS.map((m) => {
              const down = engineHealth.unavailable.has(m.key);
              const note = tr ? m.note_tr : m.note_en;
              const suffix = down ? maintenanceSuffix(tr) : note ? " — " + note : "";
              return (
                <option key={m.key} value={m.key} disabled={down}>
                  {"Model: " + (tr ? m.tr : m.en) + suffix}
                </option>
              );
            })}
          </select>
          {engineDown ? (
            <div style={{ fontSize: 11, color: "var(--mu)", marginTop: 6, lineHeight: 1.5 }}>
              {tr
                ? "Seçtiğiniz model bakımda — sağlayıcı hesabı yanıt vermiyor. Otomatik'e geçildi."
                : "Your picked model is under maintenance — its provider account isn't responding. Switched to Automatic."}
            </div>
          ) : null}
          </>
        ) : (
          <select
            value={designStyle}
            onChange={(e) => onDesignStyle(e.target.value)}
            style={{ width: "100%", marginTop: 12, padding: "9px 10px", borderRadius: 8, fontSize: 13, background: "var(--surface)", border: "1px solid var(--b2)", color: "var(--tx)" }}
          >
            {DESIGN_STYLES.map((s) => (
              <option key={s.key} value={s.key}>
                {(tr ? "Tasarım stili: " : "Design style: ") + (tr ? s.tr : s.en)}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Post type chip — Feed vs Story. Story renders 9:16 Stories on
          IG/FB; other platforms publish the content as feed posts. */}
      <div className="card" style={{ padding: 22, marginTop: 14 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          — {tr ? "Gönderi türü" : "Post type"}
        </div>
        <div
          style={{
            display: "flex",
            background: "var(--surface)",
            border: "1px solid var(--b2)",
            borderRadius: 8,
            padding: 3,
          }}
        >
          {(
            [
              [
                "feed",
                tr ? "Akış" : "Feed",
                tr ? "Tüm platformlar · Kalıcı gönderi" : "All platforms · Permanent post",
              ],
              [
                "story",
                tr ? "Hikaye" : "Story",
                tr ? "IG + FB hikaye · 24 saat" : "IG + FB story · 24h",
              ],
            ] as const
          ).map(([id, label, sub]) => (
            <button
              key={id}
              onClick={() => onPostType(id)}
              style={{
                flex: 1,
                padding: "10px 12px",
                borderRadius: 6,
                fontSize: 13,
                background: postType === id ? "var(--elev)" : "transparent",
                color: postType === id ? "var(--tx)" : "var(--tx2)",
                border:
                  postType === id ? "1px solid var(--b3)" : "1px solid transparent",
                fontWeight: postType === id ? 600 : 500,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span>{label}</span>
              <span style={{ fontSize: 10, color: "var(--mu)", fontFamily: "var(--mono)" }}>
                {sub}
              </span>
            </button>
          ))}
        </div>
        {postType === "story" ? (
          <div style={{ fontSize: 11, color: "var(--warning)", marginTop: 10 }}>
            {tr
              ? "Hikayeler Instagram + Facebook'ta yayınlanır; diğer seçili platformlarda içerik akış gönderisi olarak paylaşılır."
              : "Stories publish on Instagram + Facebook; on other selected platforms the content will be posted as a feed post."}
          </div>
        ) : null}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
        <button className="btn btn-secondary" onClick={onBack}>
          ← {tr ? "Geri" : "Back"}
        </button>
        <button className="btn btn-primary" onClick={onNext}>
          {tr ? "Devam" : "Continue"} <Icons.ArrowRight size={13} />
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Platform mix ──────────────────────────────────────────────────

function MixStep({
  brand,
  counts,
  onChange,
  total,
  onBack,
  onRun,
  submitting,
  submitError,
  tr,
}: {
  brand: Brand | null;
  counts: Record<string, number>;
  onChange: (c: Record<string, number>) => void;
  total: number;
  onBack: () => void;
  onRun: () => void;
  submitting: boolean;
  submitError: string | null;
  tr: boolean;
}) {
  const connected = new Set(brand?.connected_platforms ?? []);
  const platformsActive = Object.values(counts).filter((v) => v > 0).length;
  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontSize: 22, marginBottom: 6 }}>
        {tr ? "Platform başına kaç gönderi?" : "How many posts per platform?"}
      </h2>
      <p style={{ color: "var(--tx2)", marginBottom: 24 }}>
        {tr
          ? `${total} gönderi · ${platformsActive} platform.`
          : `${total} posts total across ${platformsActive} platforms.`}
      </p>
      <div className="card" style={{ padding: 22 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {ALL_PLATFORMS.map((id) => {
            const meta = PLATFORM_META[id];
            const isConnected = connected.size === 0 || connected.has(id);
            const value = counts[id] ?? 0;
            return (
              <div
                key={id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  opacity: isConnected ? 1 : 0.4,
                }}
              >
                <PlatformChip platform={id} size={36} active={isConnected} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{meta.name}</div>
                  <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                    {isConnected ? (tr ? "bağlı" : "connected") : tr ? "bağlı değil" : "not connected"}
                  </div>
                </div>
                <button
                  onClick={() =>
                    isConnected &&
                    onChange({ ...counts, [id]: Math.max(0, value - 1) })
                  }
                  className="btn btn-secondary"
                  style={{ padding: "4px 10px" }}
                  disabled={!isConnected}
                >
                  −
                </button>
                <div
                  className="t-num"
                  style={{
                    fontSize: 20,
                    width: 40,
                    textAlign: "center",
                    color: value > 0 ? "var(--accent)" : "var(--mu)",
                  }}
                >
                  {value}
                </div>
                <button
                  onClick={() =>
                    isConnected && onChange({ ...counts, [id]: value + 1 })
                  }
                  className="btn btn-secondary"
                  style={{ padding: "4px 10px" }}
                  disabled={!isConnected}
                >
                  +
                </button>
              </div>
            );
          })}
        </div>
        <div
          style={{
            marginTop: 22,
            padding: 14,
            background: "var(--base)",
            border: "1px solid var(--b1)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Icons.Info size={14} style={{ color: "var(--info)" }} />
          <div style={{ fontSize: 12, color: "var(--tx2)", flex: 1 }}>
            <b style={{ color: "var(--tx)" }}>
              {total} {tr ? "gönderi" : "posts"}
            </b>{" "}
            ·{" "}
            {tr
              ? `tahmini AI maliyeti $${(total * 0.18).toFixed(2)}`
              : `est. AI cost $${(total * 0.18).toFixed(2)}`}
          </div>
        </div>
      </div>

      {submitError ? (
        <p style={{ color: "var(--error)", fontSize: 12, marginTop: 12 }} role="alert">
          {submitError}
        </p>
      ) : null}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
        <button className="btn btn-secondary" onClick={onBack} disabled={submitting}>
          ← {tr ? "Geri" : "Back"}
        </button>
        <button
          className="btn btn-primary"
          onClick={onRun}
          disabled={submitting || total === 0}
        >
          <Icons.Zap size={14} />{" "}
          {submitting
            ? tr
              ? "Başlatılıyor…"
              : "Starting…"
            : tr
              ? `Toplu işi başlat · ${total} gönderi`
              : `Run bulk job · ${total} posts`}
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Running + progress (polled) ───────────────────────────────────

function RunStep({
  jobId,
  brand,
  counts,
  total,
  tr,
}: {
  jobId: string;
  brand: Brand;
  counts: Record<string, number>;
  total: number;
  tr: boolean;
}) {
  const navigate = useNavigate();
  const { data: job } = useQuery({
    queryKey: ["bulk-job", jobId],
    queryFn: () => api<BulkJob>(`/bulk-jobs/${jobId}`),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      if (s === "completed" || s === "partial" || s === "failed") return false;
      return 5_000;
    },
  });

  useEffect(() => {
    // Side-effect free placeholder — preserved so future "auto-advance to done"
    // logic can hang off completion.
  }, [job?.status]);

  const stats = job?.child_stats;
  // Backend stats fields are: pending / running / ready / approved /
  // failed (NOT `completed`). v0.1.66 — previous code read
  // stats.completed which was always undefined → progress bar stuck
  // at 0% even as children finished. A child is "done" when status
  // is ready, approved, or failed.
  const totalReported =
    stats?.total ??
    (stats
      ? stats.pending + stats.running + (stats.ready ?? 0) + (stats.approved ?? 0) + stats.failed
      : total);
  const completed = (stats?.ready ?? 0) + (stats?.approved ?? 0) + (stats?.failed ?? 0);
  const pct = totalReported > 0 ? Math.round((completed / totalReported) * 100) : 0;
  const done =
    job?.status === "completed" ||
    job?.status === "partial" ||
    job?.status === "failed";

  const accent = brand.accent_hex || "var(--accent)";
  const accentSoft = brand.accent_soft_hex || "var(--accent-soft)";

  return (
    <div style={{ maxWidth: 880 }}>
      <div
        className="card"
        style={{ padding: 28, marginBottom: 16, position: "relative", overflow: "hidden" }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 300,
            height: 300,
            background: `radial-gradient(circle, ${accentSoft}, transparent 70%)`,
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />
        <div style={{ position: "relative" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 18,
            }}
          >
            <Rosette size={42} stroke={accent} spin={!done} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="eyebrow" style={{ marginBottom: 6 }}>
                — {tr ? "Toplu" : "Bulk"} · {brand.trade_name}
              </div>
              <h2 style={{ fontSize: 22 }}>
                {done
                  ? tr
                    ? "Tamam — varyantları gözden geçir"
                    : "Done — review variants"
                  : tr
                    ? "Ay planı üretiliyor…"
                    : "Generating month plan…"}
              </h2>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="t-num" style={{ fontSize: 32, color: accent, lineHeight: 1 }}>
                {completed}
                <span style={{ fontSize: 16, color: "var(--mu)" }}>/{totalReported}</span>
              </div>
              <div
                className="mono"
                style={{ fontSize: 10, color: "var(--mu)", marginTop: 4 }}
              >
                {pct}% {tr ? "tamam" : "complete"}
              </div>
            </div>
          </div>

          <div
            style={{
              height: 6,
              background: "var(--elev)",
              borderRadius: 3,
              overflow: "hidden",
              marginBottom: 18,
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                background: accent,
                transition: "width 280ms",
              }}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 10,
            }}
          >
            {ALL_PLATFORMS.map((id) => {
              const meta = PLATFORM_META[id];
              const t = counts[id] || 0;
              const proportion = totalReported > 0 ? t / totalReported : 0;
              const partProgress = Math.min(t, Math.round(completed * proportion));
              return (
                <div
                  key={id}
                  style={{
                    padding: 12,
                    background: "var(--base)",
                    border: "1px solid var(--b1)",
                    borderRadius: 8,
                    opacity: t > 0 ? 1 : 0.4,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 6,
                    }}
                  >
                    <PlatformIcon platform={id} size={14} />
                    <span style={{ fontSize: 12, fontWeight: 500 }}>{meta.short}</span>
                  </div>
                  <div className="t-num" style={{ fontSize: 18 }}>
                    {partProgress}
                    <span style={{ fontSize: 10, color: "var(--mu)" }}>/{t}</span>
                  </div>
                  <div
                    style={{
                      height: 3,
                      background: "var(--elev)",
                      borderRadius: 2,
                      overflow: "hidden",
                      marginTop: 6,
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: t > 0 ? `${(partProgress / t) * 100}%` : "0%",
                        background: meta.color,
                        transition: "width 200ms",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {!done ? (
        <div
          style={{
            display: "flex",
            gap: 14,
            alignItems: "center",
            padding: 16,
            background: "var(--surface)",
            border: "1px solid var(--b2)",
            borderRadius: 10,
          }}
        >
          <Icons.Info size={14} style={{ color: "var(--info)" }} />
          <div style={{ fontSize: 13, flex: 1 }}>
            {tr
              ? "Arka planda çalışıyor — bu sayfadan ayrılabilirsin. Hazır olduğunda bildiririz."
              : "Running in the background — you can leave this page. We'll notify you when ready."}
          </div>
        </div>
      ) : (
        <div
          className="card"
          style={{ padding: 22, display: "flex", alignItems: "center", gap: 16 }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              background: "rgba(34,197,94,0.15)",
              color: "var(--success)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icons.Check size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {tr
                ? `${completed} varyant gözden geçirmeye hazır`
                : `${completed} variants ready to review`}
            </div>
            <div style={{ fontSize: 12, color: "var(--tx2)", marginTop: 2 }}>
              {tr
                ? "Hepsini onayla, tek tek düzenle veya tüm parti için zaman çizelgesi oluştur."
                : "Bulk-approve, edit individually, or schedule the whole batch."}
            </div>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate({ to: "/app/calendar" })}
          >
            {tr ? "Takvime git" : "Go to calendar"}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate({ to: "/app/inbox" })}
          >
            <Icons.Eye size={13} /> {tr ? "İncele" : "Review"} · {completed}
          </button>
        </div>
      )}
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────

// Best-practice weekly posting cadence per platform (a light social-media
// strategy default). The bulk wizard multiplies this by the number of weeks
// in the chosen date range to suggest per-platform post counts — so the
// numbers track the range + platform norms instead of being fixed.
const PLATFORM_WEEKLY: Record<string, number> = {
  instagram: 4, // ~4-5/wk
  facebook: 3, // ~3-4/wk
  x: 7, // ~1/day, X rewards frequency
  linkedin: 3, // ~3/wk
  telegram: 5, // ~5/wk
};

// daysBetween — inclusive day span of a start→end ISO range (min 1).
function daysBetween(startISO: string, endISO: string): number {
  const ms = new Date(endISO).getTime() - new Date(startISO).getTime();
  if (!Number.isFinite(ms)) return 30;
  return Math.max(1, Math.round(ms / 86_400_000));
}

// suggestedCounts — days × per-platform weekly cadence → whole-number counts.
function suggestedCounts(days: number): Record<string, number> {
  const weeks = Math.max(1, days) / 7;
  const out: Record<string, number> = {};
  for (const [platform, perWeek] of Object.entries(PLATFORM_WEEKLY)) {
    out[platform] = Math.max(0, Math.round(weeks * perWeek));
  }
  return out;
}

function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
